import { AxiosResponse } from 'axios';
import { client } from '../../shared/client';
import { filterURL, url } from '../../shared/URL';
import {
  AccrualSettings,
  defaultPageRequest,
  LoyaltyCategory,
  Mission,
  PageRequest,
} from './types';

export type ListResult<T> = {
  items: T[];
  totalElements: number;
};

const extractList = <T>(response: AxiosResponse): ListResult<T> => {
  const payload = response && response.data && response.data.payload;
  if (!payload) {
    return { items: [], totalElements: 0 };
  }
  if (Array.isArray(payload)) {
    return { items: payload, totalElements: payload.length };
  }
  if (Array.isArray(payload.data)) {
    return {
      items: payload.data,
      totalElements:
        payload.totalElements !== undefined
          ? payload.totalElements
          : payload.data.length,
    };
  }
  if (Array.isArray(payload.content)) {
    return {
      items: payload.content,
      totalElements:
        payload.totalElements !== undefined
          ? payload.totalElements
          : payload.content.length,
    };
  }
  return { items: [], totalElements: 0 };
};

const extractOne = <T>(response: AxiosResponse): T | null => {
  const payload = response && response.data && response.data.payload;
  if (!payload) {
    return null;
  }
  if (payload.data && !Array.isArray(payload.data)) {
    return payload.data as T;
  }
  if (Array.isArray(payload.data) && payload.data.length) {
    return payload.data[0] as T;
  }
  return payload as T;
};

const withFilters = (basePath: string, filters?: string): string => {
  if (!filters) {
    return basePath;
  }
  return `${basePath}${filterURL.prefix}${filters}`;
};

/** Pagination as query string — browsers/nginx strip body on GET. */
const pageParams = (page: PageRequest = defaultPageRequest) => ({
  offset: page.offset,
  size: page.size,
  sortBy: page.sortBy,
  sortDirection: page.sortDirection,
});

/**
 * POST /loyalty/all + JSON body Pagination (same pattern as /missions/all).
 * GET+@RequestBody is unreliable in browsers (body stripped → 400).
 */
export const fetchLoyaltyCategories = (
  page: PageRequest = defaultPageRequest,
  filters?: string
): Promise<ListResult<LoyaltyCategory>> => {
  const path = withFilters(url.loyaltyAll, filters);
  return client
    .post(path, page)
    .then((response) => extractList<LoyaltyCategory>(response));
};

export const createLoyaltyCategory = (
  body: Omit<LoyaltyCategory, 'id'>
): Promise<AxiosResponse> => client.post(url.loyaltyCreate, body);

export const updateLoyaltyCategory = (
  id: number,
  body: Omit<LoyaltyCategory, 'id'>
): Promise<AxiosResponse> => client.put(`${url.loyaltyUpdate}${id}`, body);

export const deleteLoyaltyCategory = (id: number): Promise<AxiosResponse> =>
  client.delete(`${url.loyaltyDelete}${id}`);

export const exportLoyaltyCategories = (): Promise<AxiosResponse> =>
  client.get(url.loyaltyExport, { responseType: 'blob' });

export const fetchAccrualSettings = (
  page: PageRequest = defaultPageRequest,
  filters?: string
): Promise<ListResult<AccrualSettings>> =>
  client
    .post(withFilters(url.accrualSettingsAll, filters), page)
    .then((response) => extractList<AccrualSettings>(response));

export const createAccrualSettings = (
  body: Omit<AccrualSettings, 'id'>
): Promise<AxiosResponse> => client.post(url.accrualSettingsCreate, body);

export const updateAccrualSettings = (
  id: number,
  body: Omit<AccrualSettings, 'id'>
): Promise<AxiosResponse> =>
  client.put(`${url.accrualSettingsUpdate}${id}`, body);

export const exportAccrualSettings = (): Promise<AxiosResponse> =>
  client.get(url.accrualSettingsExport, { responseType: 'blob' });

// --- Missions (POST list with body — supported by API) ---

export const fetchMissions = (
  page: PageRequest = defaultPageRequest
): Promise<ListResult<Mission>> =>
  client
    .post(url.missionsAll, page)
    .then((response) => extractList<Mission>(response));

export const fetchMissionById = (id: number): Promise<Mission | null> =>
  client
    .get(`${url.missionsGet}${id}`, { params: pageParams() })
    .then((response) => extractOne<Mission>(response));

export const createMission = (
  body: Omit<Mission, 'id'>
): Promise<AxiosResponse> => client.post(url.missionsCreate, body);

export const updateMission = (
  id: number,
  body: Omit<Mission, 'id'>
): Promise<AxiosResponse> => client.put(`${url.missionsUpdate}${id}`, body);

export const deleteMission = (id: number): Promise<AxiosResponse> =>
  client.delete(`${url.missionsDelete}${id}`);

// --- Category ↔ Mission links ---

export const fetchMissionsByCategory = (
  categoryId: number,
  page: PageRequest = defaultPageRequest
): Promise<ListResult<Mission>> =>
  client
    .get(`${url.categoryMissionsByCategory}${categoryId}`, {
      params: pageParams(page),
    })
    .then((response) => extractList<Mission>(response));

export const linkCategoryMission = (
  categoryId: number,
  missionId: number
): Promise<AxiosResponse> =>
  client.post(`${url.categoryMissionLink}${categoryId}/mission/${missionId}`);

export const unlinkCategoryMission = (
  categoryId: number,
  missionId: number
): Promise<AxiosResponse> =>
  client.delete(`${url.categoryMissionLink}${categoryId}/mission/${missionId}`);
